import { useEffect, useState } from "react";
import { api, fmtMoney, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Plus, Trash2, Pencil, Copy, Upload, Download } from "lucide-react";
import { toast } from "sonner";

const empty = {
  name: "", email: "", employee_code: "", department: "Engineering", designation: "",
  joining_date: "", basic: 0, da: 0, hra: 0, allowances: 0, deductions: 0, tax_percent: 0,
  state: "", pan: "", enable_pf: false, enable_esi: false,
  create_login: true, password: "",
};

export default function EmployeesPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [issuedCreds, setIssuedCreds] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkCsv, setBulkCsv] = useState("");
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/employees");
      setRows(data);
    } catch (e) { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditingId(null); setForm(empty); setOpen(true); };
  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      ...empty, name: row.name, email: row.email, employee_code: row.employee_code || "",
      department: row.department, designation: row.designation, joining_date: row.joining_date,
      basic: row.basic, hra: row.hra, allowances: row.allowances, deductions: row.deductions,
      tax_percent: row.tax_percent, create_login: false,
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.patch(`/employees/${editingId}`, {
          name: form.name, department: form.department, designation: form.designation,
          basic: Number(form.basic), da: Number(form.da), hra: Number(form.hra),
          allowances: Number(form.allowances), deductions: Number(form.deductions),
          tax_percent: Number(form.tax_percent), state: form.state, pan: form.pan,
          enable_pf: form.enable_pf, enable_esi: form.enable_esi,
        });
        toast.success("Employee updated");
      } else {
        const { data } = await api.post("/employees", {
          ...form,
          email: form.email.trim().toLowerCase(),
          basic: Number(form.basic), da: Number(form.da), hra: Number(form.hra),
          allowances: Number(form.allowances), deductions: Number(form.deductions),
          tax_percent: Number(form.tax_percent),
        });
        if (data._default_password) {
          setIssuedCreds({ email: data.email, password: data._default_password });
        }
        toast.success("Employee added");
      }
      setOpen(false);
      setForm(empty);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Failed to save employee"));
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this employee?")) return;
    try {
      await api.delete(`/employees/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail, "Failed to delete")); }
  };

  const copyCreds = () => { navigator.clipboard.writeText(`${issuedCreds.email} / ${issuedCreds.password}`); toast.success("Copied"); };

  const CSV_TEMPLATE = "name,email,department,designation,basic,da,hra,allowances,deductions,tax_percent,state,pan,enable_pf,enable_esi,password\nRahul Sharma,rahul@example.com,Engineering,Manager,30000,3000,12000,5000,0,0,haryana,ABCDE1234F,true,false,Welcome@2026\nPriya Patel,priya@example.com,Finance,Analyst,25000,2500,10000,4000,0,0,maharashtra,,true,true,";

  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "employees_template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const parseCsv = (text) => {
    const lines = text.trim().split(/\r?\n/).filter(Boolean);
    if (lines.length < 2) throw new Error("CSV needs a header row and at least one data row.");
    const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
    const required = ["name", "email"];
    for (const r of required) if (!headers.includes(r)) throw new Error(`Missing required column: ${r}`);
    const rows = [];
    for (let i = 1; i < lines.length; i++) {
      const cells = lines[i].split(",").map((c) => c.trim());
      const row = {};
      headers.forEach((h, idx) => { row[h] = cells[idx] ?? ""; });
      rows.push({
        name: row.name,
        email: (row.email || "").toLowerCase(),
        department: row.department || "General",
        designation: row.designation || "Staff",
        basic: Number(row.basic || 0),
        da: Number(row.da || 0),
        hra: Number(row.hra || 0),
        allowances: Number(row.allowances || 0),
        deductions: Number(row.deductions || 0),
        tax_percent: Number(row.tax_percent || 0),
        state: (row.state || "").toLowerCase(),
        pan: (row.pan || "").toUpperCase(),
        enable_pf: /^(true|1|yes|y)$/i.test(row.enable_pf || ""),
        enable_esi: /^(true|1|yes|y)$/i.test(row.enable_esi || ""),
        create_login: true,
        password: row.password || "",
      });
    }
    return rows;
  };

  const submitBulk = async () => {
    setBulkResult(null);
    let employees;
    try { employees = parseCsv(bulkCsv); }
    catch (e) { toast.error(e.message); return; }
    if (!employees.length) return;
    setBulkLoading(true);
    try {
      const { data } = await api.post("/employees/bulk", { employees });
      setBulkResult(data);
      toast.success(`${data.created_count} employees imported${data.error_count ? `, ${data.error_count} skipped` : ""}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Bulk import failed"));
    } finally { setBulkLoading(false); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div>
      <PageHeader
        testid="page-employees"
        eyebrow="Roster"
        title="Employees."
        description="Add, edit and manage the workforce for this client. Each employee gets a login and portal access."
        actions={
          <div className="flex items-center gap-2">
            <button onClick={() => { setBulkOpen(true); setBulkResult(null); }} data-testid="btn-bulk-import"
              className="inline-flex items-center gap-2 border border-[#C9A961] px-4 py-2.5 font-mono text-sm hover:bg-neutral-950 hover:text-[#D4B87D] transition-colors">
              <Upload className="w-4 h-4" /> Bulk import
            </button>
            <button onClick={openNew} data-testid="btn-add-employee"
              className="inline-flex items-center gap-2 bg-neutral-950 text-[#D4B87D] px-4 py-2.5 font-mono text-sm hover:bg-[#C9A961] hover:text-neutral-950 border border-neutral-950 hover:border-[#C9A961] transition-colors">
              <Plus className="w-4 h-4" /> New employee
            </button>
          </div>
        }
      />

      {issuedCreds && (
        <div className="mx-8 mt-6 border border-neutral-950 bg-neutral-950 text-white p-6" data-testid="issued-creds-banner">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#FF3B30] mb-2">Employee login issued — save this</div>
          <div className="flex flex-wrap items-center gap-6">
            <div><div className="text-xs font-mono text-neutral-400">Email</div><div className="font-mono">{issuedCreds.email}</div></div>
            <div><div className="text-xs font-mono text-neutral-400">Password</div><div className="font-mono">{issuedCreds.password}</div></div>
            <button onClick={copyCreds} className="ml-auto flex items-center gap-2 px-3 py-2 border border-white/30 hover:bg-white hover:text-neutral-950 font-mono text-xs transition-colors">
              <Copy className="w-3.5 h-3.5" /> Copy
            </button>
            <button onClick={() => setIssuedCreds(null)} className="px-3 py-2 border border-white/30 hover:bg-white hover:text-neutral-950 font-mono text-xs transition-colors">Dismiss</button>
          </div>
        </div>
      )}

      <div className="p-8">
        <div className="border border-neutral-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="employees-table">
            <thead className="bg-[#FAFAFA] border-b border-neutral-200">
              <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Department</th>
                <th className="px-4 py-3">Designation</th>
                <th className="px-4 py-3 text-right">Basic</th>
                <th className="px-4 py-3 text-right">Gross</th>
                <th className="px-4 py-3 text-right">Net</th>
                <th className="px-4 py-3 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="p-6 text-neutral-500 font-mono">Loading…</td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={8} className="p-8 text-neutral-500 font-mono">No employees yet. Click <strong>New employee</strong>.</td></tr>
              )}
              {rows.map((r) => {
                const gross = Number(r.basic || 0) + Number(r.hra || 0) + Number(r.allowances || 0);
                const tax = gross * Number(r.tax_percent || 0) / 100;
                const net = gross - Number(r.deductions || 0) - tax;
                return (
                  <tr key={r.id} className="border-b border-neutral-100 hover:bg-[#FAFAFA] transition-colors" data-testid={`row-emp-${r.id}`}>
                    <td className="px-4 py-3 font-mono text-xs">{r.employee_code}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold">{r.name}</div>
                      <div className="text-xs text-neutral-500 font-mono">{r.email}</div>
                    </td>
                    <td className="px-4 py-3 font-mono">{r.department}</td>
                    <td className="px-4 py-3 font-mono">{r.designation}</td>
                    <td className="px-4 py-3 text-right font-mono">{fmtMoney(r.basic)}</td>
                    <td className="px-4 py-3 text-right font-mono">{fmtMoney(gross)}</td>
                    <td className="px-4 py-3 text-right font-mono font-semibold text-[#002FA7]">{fmtMoney(net)}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => openEdit(r)} data-testid={`btn-edit-${r.id}`} className="inline-flex items-center gap-1 text-neutral-500 hover:text-neutral-950 font-mono text-xs mr-3 transition-colors">
                        <Pencil className="w-3.5 h-3.5" /> Edit
                      </button>
                      <button onClick={() => remove(r.id)} data-testid={`btn-delete-${r.id}`} className="inline-flex items-center gap-1 text-neutral-500 hover:text-[#FF3B30] font-mono text-xs transition-colors">
                        <Trash2 className="w-3.5 h-3.5" /> Remove
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {bulkOpen && (
        <div className="fixed inset-0 bg-neutral-950/60 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setBulkOpen(false)}>
          <div className="bg-white border border-[#C9A961] w-full max-w-3xl my-8" onClick={(e) => e.stopPropagation()} data-testid="dialog-bulk-import">
            <div className="border-b border-neutral-200 px-6 py-4 flex items-center justify-between">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#8C6F1E]">CSV · Bulk</div>
                <div className="font-serif font-bold text-2xl tracking-tight">Import employees</div>
              </div>
              <button onClick={() => setBulkOpen(false)} className="text-neutral-500 hover:text-neutral-950 font-mono">✕</button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-xs text-neutral-600 font-mono leading-relaxed">
                  Header row required. Columns: <span className="text-neutral-950">name, email, department, designation, basic, da, hra, allowances, deductions, tax_percent, state, pan, enable_pf, enable_esi, password</span>. Only <span className="text-neutral-950">name</span> and <span className="text-neutral-950">email</span> are required.
                </div>
                <button onClick={downloadTemplate} data-testid="btn-download-template"
                  className="inline-flex items-center gap-2 border border-[#C9A961] px-3 py-2 font-mono text-xs hover:bg-neutral-950 hover:text-[#D4B87D] transition-colors">
                  <Download className="w-3.5 h-3.5" /> Sample CSV
                </button>
              </div>
              <textarea
                value={bulkCsv}
                onChange={(e) => setBulkCsv(e.target.value)}
                data-testid="bulk-csv"
                placeholder="Paste CSV here…"
                rows={10}
                className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#C9A961] outline-none font-mono text-xs whitespace-pre"
              />
              <div className="flex items-center gap-3">
                <input type="file" accept=".csv,text/csv" data-testid="bulk-file"
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    const text = await f.text();
                    setBulkCsv(text);
                  }}
                  className="text-xs font-mono" />
                <span className="text-xs text-neutral-500 font-mono">or paste directly above</span>
              </div>

              {bulkResult && (
                <div className="border border-neutral-200 bg-[#FAF7EF] p-4" data-testid="bulk-result">
                  <div className="font-mono text-xs">
                    <span className="text-[#8C6F1E] font-semibold">{bulkResult.created_count} created</span>
                    {bulkResult.error_count > 0 && <span className="text-[#B33A1F]">  ·  {bulkResult.error_count} skipped</span>}
                  </div>
                  {bulkResult.errors?.length > 0 && (
                    <ul className="mt-2 space-y-1 text-xs font-mono text-[#B33A1F] max-h-40 overflow-y-auto">
                      {bulkResult.errors.map((er, i) => (
                        <li key={`err-${er.row}-${er.email || i}`}>Row {er.row}{er.email ? ` (${er.email})` : ""}: {er.error}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-2">
                <button type="button" onClick={() => setBulkOpen(false)} className="px-4 py-2.5 font-mono text-sm hover:bg-neutral-100 transition-colors">Close</button>
                <button onClick={submitBulk} disabled={bulkLoading || !bulkCsv.trim()} data-testid="btn-submit-bulk"
                  className="px-5 py-2.5 bg-neutral-950 text-[#D4B87D] font-mono text-sm hover:bg-[#C9A961] hover:text-neutral-950 border border-neutral-950 hover:border-[#C9A961] transition-colors disabled:opacity-50">
                  {bulkLoading ? "Importing…" : "Import"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-neutral-950/50 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setOpen(false)}>
          <div className="bg-white border border-neutral-950 w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()} data-testid="dialog-employee">
            <div className="border-b border-neutral-200 px-6 py-4 flex items-center justify-between">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#002FA7]">{editingId ? "Update" : "Onboard"}</div>
                <div className="font-display font-bold text-2xl tracking-tighter">{editingId ? "Edit employee" : "New employee"}</div>
              </div>
              <button onClick={() => setOpen(false)} className="text-neutral-500 hover:text-neutral-950 font-mono">✕</button>
            </div>
            <form onSubmit={submit} className="p-6 grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Full name *</label>
                <input required value={form.name} onChange={set("name")} data-testid="emp-name"
                  className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#002FA7] outline-none font-mono text-sm" />
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Email *</label>
                <input required type="email" value={form.email} onChange={set("email")} disabled={!!editingId} data-testid="emp-email"
                  className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#002FA7] outline-none font-mono text-sm disabled:bg-neutral-100" />
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Department</label>
                <input value={form.department} onChange={set("department")} data-testid="emp-dept"
                  className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#002FA7] outline-none font-mono text-sm" />
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Designation</label>
                <input value={form.designation} onChange={set("designation")} data-testid="emp-designation"
                  className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#002FA7] outline-none font-mono text-sm" />
              </div>
              {!editingId && (
                <>
                  <div>
                    <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Employee code (optional)</label>
                    <input value={form.employee_code} onChange={set("employee_code")}
                      className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#002FA7] outline-none font-mono text-sm" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Joining date</label>
                    <input type="date" value={form.joining_date} onChange={set("joining_date")}
                      className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#002FA7] outline-none font-mono text-sm" />
                  </div>
                </>
              )}
              <div className="col-span-2 border-t border-neutral-200 pt-4 mt-2 text-[10px] font-mono uppercase tracking-widest text-neutral-500">
                Salary structure (monthly)
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Basic</label>
                <input type="number" step="0.01" value={form.basic} onChange={set("basic")} data-testid="emp-basic"
                  className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#002FA7] outline-none font-mono text-sm" />
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">DA (Dearness Allowance)</label>
                <input type="number" step="0.01" value={form.da} onChange={set("da")} data-testid="emp-da"
                  className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#002FA7] outline-none font-mono text-sm" />
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">HRA</label>
                <input type="number" step="0.01" value={form.hra} onChange={set("hra")} data-testid="emp-hra"
                  className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#002FA7] outline-none font-mono text-sm" />
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Allowances</label>
                <input type="number" step="0.01" value={form.allowances} onChange={set("allowances")} data-testid="emp-allowances"
                  className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#002FA7] outline-none font-mono text-sm" />
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Other deductions</label>
                <input type="number" step="0.01" value={form.deductions} onChange={set("deductions")} data-testid="emp-deductions"
                  className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#002FA7] outline-none font-mono text-sm" />
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Tax % (simple regime)</label>
                <input type="number" step="0.01" value={form.tax_percent} onChange={set("tax_percent")} data-testid="emp-tax-percent"
                  className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#002FA7] outline-none font-mono text-sm" />
              </div>

              <div className="col-span-2 border-t border-neutral-200 pt-4 mt-2 text-[10px] font-mono uppercase tracking-widest text-neutral-500">
                India statutory (used when company regime = India)
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Work state (for PT & LWF)</label>
                <select value={form.state} onChange={set("state")} data-testid="emp-state"
                  className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#002FA7] outline-none font-mono text-sm bg-white">
                  <option value="">— none —</option>
                  {["Maharashtra","Karnataka","Delhi","Tamil Nadu","Gujarat","Telangana","Andhra Pradesh","West Bengal","Kerala","Punjab","Haryana","Odisha","Bihar","Assam","Madhya Pradesh","Jharkhand","Goa"].map((s) => (
                    <option key={s} value={s.toLowerCase()}>{s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">PAN (optional)</label>
                <input value={form.pan} onChange={set("pan")} data-testid="emp-pan" placeholder="ABCDE1234F"
                  className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#002FA7] outline-none font-mono text-sm uppercase" />
              </div>
              <div className="col-span-2 flex gap-6 pt-1">
                <label className="flex items-center gap-2 text-sm font-mono cursor-pointer">
                  <input type="checkbox" checked={form.enable_pf} onChange={(e) => setForm({...form, enable_pf: e.target.checked})} data-testid="emp-enable-pf" />
                  Deduct EPF (12% of Basic + DA)
                </label>
                <label className="flex items-center gap-2 text-sm font-mono cursor-pointer">
                  <input type="checkbox" checked={form.enable_esi} onChange={(e) => setForm({...form, enable_esi: e.target.checked})} data-testid="emp-enable-esi" />
                  Deduct ESI (if gross ≤ ₹21,000)
                </label>
              </div>

              {!editingId && (
                <div className="col-span-2 border-t border-neutral-200 pt-4">
                  <label className="flex items-center gap-2 text-sm font-mono cursor-pointer">
                    <input type="checkbox" checked={form.create_login} onChange={(e) => setForm({...form, create_login: e.target.checked})} data-testid="emp-create-login" />
                    Create login account (default password: Welcome@2026)
                  </label>
                </div>
              )}

              <div className="col-span-2 flex items-center justify-end gap-2 pt-2">
                <button type="button" onClick={() => setOpen(false)} className="px-4 py-2.5 font-mono text-sm hover:bg-neutral-100 transition-colors">Cancel</button>
                <button type="submit" data-testid="btn-submit-employee" className="px-5 py-2.5 bg-[#002FA7] text-white font-mono text-sm hover:bg-neutral-950 transition-colors">
                  {editingId ? "Save changes" : "Add employee"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
